import { createFormContext } from "@mantine/form";
import { addMinutes, isBefore } from "date-fns";

import { ipFilterError } from "@/features/settings/components/individual-assessment/exam-settings/parse-ip-filter";
import {
  AssessmentType,
  ExamTiming,
  RequirementsFragment,
} from "@/generated/graphql";

const [SettingsFormProvider, useSettingsFormContext, useForm] =
  createFormContext<RequirementsFragment>();

const vivaMinimumMessage = (vivaMinimumMinutes: number) =>
  `Must be at least ${vivaMinimumMinutes} minutes to fit the video response questions`;

interface ExtendedRequirements extends RequirementsFragment {
  vivaMinimumMinutes?: number;
}

/**
 * Given initial requirements. It returns a stateful settings
 * form with validation set up.
 *
 * `vivaMinimumMinutes` is the shortest time limit or exam duration that fits
 * the video response questions, from `summarizeVivaTime`.
 */
function useSettingsForm(extendedRequirements: ExtendedRequirements) {
  const { vivaMinimumMinutes = 0, ...requirements } = extendedRequirements;

  return useForm({
    initialValues: {
      ...requirements,
    },
    validate: {
      examStartDate: (value) => (value != null ? null : "Select a date"),
      examEndDate: (value, { examTiming, examStartDate }) => {
        if (examTiming === ExamTiming.Live) return null;
        if (value == null) return "Select a date";
        if (examStartDate != null) {
          const examEnd = new Date(value);
          const examStart = new Date(examStartDate);
          if (isBefore(examEnd, examStart)) {
            return "Exam close must be after exam open";
          }
        }
        return null;
      },
      examReadingTime: (
        value,
        {
          noExamReadingTime,
          examStartDate,
          examTiming,
          examEndDate,
          examWritingTime,
        }
      ) => {
        if (value == null && !noExamReadingTime) return "Please add duration";
        if (examTiming === ExamTiming.Window && examEndDate != null) {
          const inferredEndDate = new Date(
            inferLiveExamEndDate({
              examReadingTime: value,
              examWritingTime,
              noExamReadingTime,
              examStartDate,
            })
          );
          if (isBefore(new Date(examEndDate), inferredEndDate)) {
            return "Cannot be longer than the exam window";
          }
        }
        return null;
      },
      examWritingTime: (
        value,
        {
          examStartDate,
          examReadingTime,
          noExamReadingTime,
          examTiming,
          examEndDate,
        }
      ) => {
        if (value == null) return "Please add duration";
        if (value < vivaMinimumMinutes) {
          return vivaMinimumMessage(vivaMinimumMinutes);
        }
        if (examTiming === ExamTiming.Window && examEndDate != null) {
          const inferredEndDate = new Date(
            inferLiveExamEndDate({
              examReadingTime,
              examWritingTime: value,
              noExamReadingTime,
              examStartDate,
            })
          );
          if (isBefore(new Date(examEndDate), inferredEndDate)) {
            return "Cannot be longer than the exam window";
          }
        }
        return null;
      },
      timeLimit: (value, { assessmentType, noTimeLimit }) =>
        assessmentType === AssessmentType.Assignment &&
        !noTimeLimit &&
        value != null &&
        value < vivaMinimumMinutes
          ? vivaMinimumMessage(vivaMinimumMinutes)
          : null,
      examInactivityTimeout: (
        value,
        { examReadingTime, noExamReadingTime }
      ) => {
        if (value == null) return null;
        if (value < 5) return "Minimum 5 minutes";
        // The verified session's `expires_at` starts counting from
        // access-code verification, but the inactivity heartbeat only
        // fires once writing begins. If reading time outlasts the
        // timeout, the session dies before the student ever writes.
        if (
          !noExamReadingTime &&
          examReadingTime != null &&
          value <= examReadingTime
        ) {
          return "Must be longer than reading time";
        }
        return null;
      },
      examIpFilter: (value, { enableExamIpFilter }) =>
        ipFilterError(value, { required: enableExamIpFilter ?? false }),
      examType: (value) =>
        value.length > 0 ? null : "You must identify an exam type",
      maxGrade: (value) => (value >= 0 && value <= 1000 ? null : "Please fill"),
      examFeedbackDate: (
        value,
        { noExamFeedbackDate, examStartDate, gradingService }
      ) => {
        if (noExamFeedbackDate || gradingService === "cadmusgrader")
          return null;
        if (value == null) return "Please fill";
        if (
          examStartDate != null &&
          isBefore(new Date(value), new Date(examStartDate))
        ) {
          return "Must be after exam open";
        }
        return null;
      },
      vivaReattemptWindowSeconds: (value) => {
        if (value === null || value === undefined) return null;
        return value >= 1 ? null : "Please add duration";
      },
    },
    validateInputOnChange: [
      "examReadingTime",
      "examWritingTime",
      "examInactivityTimeout",
      "examIpFilter",
      "maxGrade",
      "wordLimit",
      "lateSubmissionTimeLimit",
      "timeLimit",
      "weight",
      "examFeedbackDate",
      "vivaReattemptWindowSeconds",
    ],
  });
}

/**
 * Finds when the exam will end using start date + reading time + writing time.
 * NOTE: falls back to using now as start date if the input date is `null`.
 * @returns an ISO 8601 date string for when the exam will end.
 */
export const inferLiveExamEndDate = (
  reqs: Pick<
    RequirementsFragment,
    | "examStartDate"
    | "examReadingTime"
    | "examWritingTime"
    | "noExamReadingTime"
  >
): string => {
  return addMinutes(
    reqs.examStartDate != null ? new Date(reqs.examStartDate) : new Date(),
    (reqs.noExamReadingTime ? 0 : (reqs.examReadingTime ?? 0)) +
      (reqs.examWritingTime ?? 0)
  ).toISOString();
};

export { SettingsFormProvider, useSettingsForm, useSettingsFormContext };
